import Phaser from 'phaser';
import { nameText } from '../../content/names';
import { dailyScope } from '../../meta/online/boards';
import type { ChallengeList, FriendList, GhostResponse } from '../../meta/online/schema';
import { TICK_RATE } from '../../sim/constants';
import { challengeNews, loadGhost, myChallenges, myFriends, onlineOn } from '../online';
import { nicknameOf, whoText } from '../nicknames';
import { saves, todayKey } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawClubBar } from '../ui/clubChrome';
import { drawModeTiles } from '../ui/modeTiles';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, onTap, UI } from '../ui/text';
import { formatTime } from '../ui/widgets';
import { wrapLineCount } from '../ui/wrap';
import { shortDate } from './ChallengeScene';
import { raceDailyLeader } from './dailyRace';
import type { FriendData } from './FriendScene';
import type { RaceData } from './RaceScene';
import { wipeTo } from './WipeScene';

/**
 * CLUB > VERSUS (docs/ONLINE_UI.md; owner, 2026-10-29: every mode in view, simpler to read): the
 * live modes as tiles (each opens its own short setup), the friends online now (each opens their
 * card, with PLAY TOGETHER), today's Daily leader to race, and the newest challenges with the
 * whole list one tap away.
 */
export class VersusScene extends Phaser.Scene {
  private list: ChallengeList | null = null;
  private ghost: GhostResponse | null = null;
  private friends: FriendList['friends'] | null = null;
  private loadingList = true;
  private loadingGhost = true;

  constructor() {
    super('Versus');
  }

  init(): void {
    this.list = null;
    this.ghost = null;
    this.friends = null;
    this.loadingList = true;
    this.loadingGhost = true;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    if (!onlineOn()) return;
    void myChallenges().then((list) => {
      this.list = list;
      this.loadingList = false;
      this.redraw();
    });
    void loadGhost('daily', dailyScope(todayKey())).then((ghost) => {
      this.ghost = ghost;
      this.loadingGhost = false;
      this.redraw();
    });
    void myFriends().then((list) => {
      this.friends = list?.friends ?? [];
      this.redraw();
    });
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    drawHeader(this, 'CLUB', 'club');
    drawTabBar(this, 'club');
    const top = drawClubBar(this, 'versus') + 4;
    const { width, height } = this.scale.gameSize;

    if (!onlineOn()) {
      label(this, width / 2, top + 12, 'Online play is off.', 1, UI.accent).setOrigin(0.5, 0);
      label(this, width / 2, top + 26, 'Turn it on in Settings,', 1, UI.dim).setOrigin(0.5, 0);
      label(this, width / 2, top + 36, 'Online.', 1, UI.dim).setOrigin(0.5, 0);
      bevelButton(this, 6, top + 54, width - 12, 20, 'ONLINE SETTINGS', () =>
        wipeTo(this, 'Online'),
      );
      return;
    }

    // Live with friends: every mode, each to its own setup.
    label(this, 6, top, 'PLAY LIVE WITH FRIENDS', 1, UI.dim);
    // (A short screen, an iPad's 4:3: compact tiles, so the rest fits.)
    let y = drawModeTiles(
      this,
      top + 11,
      width,
      (mode) => wipeTo(this, 'Race', { mode } satisfies RaceData),
      height < 320,
    );
    y = this.drawOnline(y, width);
    y = this.drawLeader(y + 4, width);
    this.drawChallenges(y + 6, width);
  }

  /** The friends online now, each opening their card (PLAY TOGETHER is there). */
  private drawOnline(y: number, width: number): number {
    const online = (this.friends ?? []).filter((f) => f.online);
    if (this.friends === null) return y;
    if (online.length === 0) {
      label(this, 6, y, 'No friends online right now.', 1, UI.dim);
      return y + 12;
    }
    label(this, 6, y, 'ONLINE', 1, UI.dim);
    const g = this.add.graphics();
    let x = 50;
    for (const f of online) {
      const name = nicknameOf(f.player) ?? nameText(f.name);
      const w = name.length * 6 + 10;
      if (x + w > width - 4) break;
      g.fillStyle(UI.good).fillRect(x, y + 2, 4, 4);
      label(this, x + 6, y, name, 1, UI.text);
      const zone = this.add
        .zone(x - 2, y - 3, w, 13)
        .setOrigin(0)
        .setInteractive({ useHandCursor: true })
        .setData('label', name);
      onTap(zone, () =>
        wipeTo(this, 'Friend', {
          player: f.player,
          name: f.name,
          at: f.at,
          online: true,
        } satisfies FriendData),
      );
      x += w + 6;
    }
    return y + 12;
  }

  /** Today's Daily leader: their time, and RACE (against their run, as a ghost). */
  private drawLeader(y: number, width: number): number {
    const g = this.add.graphics();
    navyPanel(g, 4, y, width - 8, 30);
    label(this, 10, y + 5, "TODAY'S DAILY LEADER", 1, UI.dim);
    const ghost = this.ghost;
    if (ghost) {
      label(this, 10, y + 17, nameText(ghost.name), 1, UI.text);
      label(this, width - 52, y + 17, formatTime(ghost.value, TICK_RATE), 1, UI.accent).setOrigin(
        1,
        0,
      );
      bevelButton(this, width - 46, y + 13, 38, 14, 'RACE', () => raceDailyLeader(this, ghost));
    } else {
      const text = this.loadingGhost ? 'Loading...' : 'No one has cleared it yet.';
      label(this, 10, y + 17, text, 1, UI.dim);
    }
    return y + 30;
  }

  /** The two newest challenges in a line each, and ALL for the whole list. */
  private drawChallenges(y: number, width: number): void {
    const bottom = this.scale.gameSize.height - TABBAR_H - 4;
    const fresh = challengeNews();
    label(this, 6, y, fresh ? 'CHALLENGES (NEW)' : 'CHALLENGES', 1, fresh ? UI.accent : UI.dim);
    bevelButton(this, width - 46, y - 3, 40, 13, 'ALL >', () => wipeTo(this, 'ChallengeList'));
    y += 13;
    const list = this.list;
    if (!list || list.challenges.length === 0) {
      const text = this.loadingList
        ? 'Loading...'
        : !list
          ? "Can't reach the server right now."
          : 'Clear the Daily, then CHALLENGE on its results to send your time to friends.';
      // (As much as fits above the tab bar: the short form on a short screen.)
      const short = 'Send one from the Daily results.';
      const fits = (s: string) => y + wrapLineCount(s, width - 12) * 10 <= bottom;
      const shown = fits(text) ? text : fits(short) ? short : null;
      if (shown) label(this, 6, y, shown, 1, UI.dim).setMaxWidth(width - 12);
      return;
    }
    const me = saves().save.online.player?.id;
    for (const c of list.challenges.slice(0, 2)) {
      if (y + 11 > bottom) break;
      const sent = c.from.player === me;
      const what = sent
        ? `You, ${shortDate(c.date)}: ${c.answers.length} raced`
        : `${whoText(c.from.player, c.from.name)}`;
      label(this, 6, y, what, 1, UI.text);
      label(this, width - 6, y, formatTime(c.ticks, TICK_RATE), 1, UI.accent).setOrigin(1, 0);
      y += 11;
    }
  }
}
